import axios from 'axios';

/**
 * The monthly collateral cycle, per period (FRD §9). Every call here is an
 * action a person takes; the automatic steps (validation, base, comparison)
 * have no calls because nobody invokes them.
 */

const ROOT = '/api/credit/abf/round2/facilities';

export type StageState = 'DONE' | 'OPEN' | 'BLOCKED' | 'WAITING';
export interface Stage { state: StageState; detail: string }

export interface PeriodMetric { status: 'COMPUTED' | 'UNAVAILABLE'; value?: number; coveragePct?: number; reason?: string }
export interface TapeEvidenceScope {
  facilityCode: string; asOfDate: string; periodLabel: string; periodDeclared: boolean;
  message: string; sourceDeclarations: Record<string, string[]>; filenameDates: string[];
}

export interface PeriodTape {
  id: number; asOfDate: string; fileName: string | null; entryChannel: string | null;
  rowsReceived: number; rowsInserted: number; rowsFailed: number; rowsSkipped: number;
  /** AWAITING_MAPPING: held, not read, until its column mapping is approved (F-MK-060). */
  status?: string | null;
  collateralBalance: number; eligibleBalance: number; ineligibleBalance: number;
  dqStatus: string; authorityStatus: string;
  createdBy: string; createdAt: string; releasedBy: string | null; releasedAt: string | null; releaseNotes: string | null;
  revisionCount: number; excludedBalance: number;
  mappingProfileId: number | null; mappingStatus: string | null; facilityVersionId: number | null;
  derivedMetrics: Record<string, PeriodMetric | number | string | null>;
  errorSummary: string | null;
  evidenceScope?: TapeEvidenceScope;
}

export interface PeriodCertificate {
  id: number; asOfDate: string; sourceReference: string; sourceType: string; entryChannel: string | null;
  reportedBorrowingBase: number; reportedEligible: number | null; reportedIneligible: number | null; reportedReserve: number | null;
  createdBy: string; createdAt: string; supersedesId: number | null;
}

/**
 * One line of the borrowing-base bridge. The current payload carries `key`, `kind` and `amount`
 * (deductions negative, one line per concentration cut, `detail` naming the rule); `value` and
 * `deltaPct` are the fields of the older payload, kept optional so either shape renders.
 */
export type BridgeLineKind = 'TOTAL' | 'DEDUCTION' | 'RATE' | 'RESULT';
export interface BridgeLine {
  key?: string;
  label: string;
  kind?: BridgeLineKind;
  amount?: number | null;
  prior: number | null;
  delta: number | null;
  detail?: string | null;
  /** Older payload: the amount, and the change as a percentage of the prior. */
  value?: number | null;
  deltaPct?: number | null;
}

export interface PeriodRun {
  id: number; asOfDate: string; approvalStatus: string; createdBy: string; createdAt: string;
  approvedBy: string | null; approvedAt: string | null; approvalNotes: string | null; facilityVersionId: number | null;
  bridge: { lines: BridgeLine[]; priorRunId: number | null; priorPeriodLabel: string | null };
}

export interface VarianceLine {
  id: number; line_key: string; line_type: string; asset_identifier: string | null;
  reported_value: number | null; computed_value: number | null; variance_amount: number;
  tolerance_amount: number; materiality_amount: number; status: string; blocking: boolean; explanation: string | null;
  disposition: 'ACCEPTED' | 'CORRECTED' | 'ESCALATED' | null; disposition_reason: string | null;
  disposed_by: string | null; disposed_at: string | null; carried_from_line_id: number | null;
}

export interface PeriodComparison {
  id: number; varianceStatus: string; varianceWords: string; materialVariance: boolean; varianceBlocking: boolean;
  thresholdSource: string; toleranceAmount: number; materialityAmount: number; blockingAmount: number;
  overrideReason: string | null; createdBy: string; createdAt: string; lines: VarianceLine[];
}

export interface ReadinessGate { key: string; satisfied: boolean; label: string; fix: string | null }
export interface NextAction { code: string; label: string; owner: 'MAKER' | 'CHECKER' | 'SYSTEM'; enabled: boolean; reason: string | null }
export interface PeriodReadiness {
  phase: 'AWAITING_EVIDENCE' | 'IN_PROGRESS' | 'AWAITING_CHECKER' | 'APPROVED';
  gates: ReadinessGate[]; blockers: string[]; contributors: string[];
  actorPrepared: boolean; actorMayCheck: boolean; actorMayMake: boolean;
  canApprove: boolean; whyNotApprove: string | null; nextAction: NextAction;
}

export interface AttentionItem { key: string; severity: 'HIGH' | 'MEDIUM' | 'LOW'; title: string; detail: string; action: string; ref: number | null; evidence?: Record<string, unknown> }

export interface CollectionPeriod {
  id: number; label: string; periodStart: string; periodEnd: string;
  determinationDate: string | null; paymentDate: string | null; deliverablesDueDate: string | null; status: string;
  tape: PeriodTape | null; certificate: PeriodCertificate | null; run: PeriodRun | null; comparison: PeriodComparison | null;
  stages: Record<'received' | 'validated' | 'released' | 'computed' | 'certificate' | 'compared' | 'approved', Stage>;
  readiness: PeriodReadiness; attention: AttentionItem[];
  history: { tapes: Record<string, unknown>[]; certificates: Record<string, unknown>[]; runs: Record<string, unknown>[]; comparisons: Record<string, unknown>[] };
}

export interface VariancePolicy {
  id: number; scope: 'TENANT_DEFAULT' | 'FACILITY'; source: string; sourceReference: string | null;
  tolerancePct: number | null; toleranceAmount: number | null; materialityPct: number | null; materialityAmount: number | null;
  blockingPct: number | null; blockingAmount: number | null; effectiveFrom: string; versionNo: number;
}

export interface Availability {
  approvedBase: number | null; commitment: number; drawn: number; reserved: number; holds: number;
  drawable: number | null; reason: string | null;
  authority: { runId: number; periodId: number | null; periodLabel: string | null; asOfDate: string; borrowingBase: number;
    approvedBy: string; approvedAt: string } | null;
}

/** F-MK-062: a maker's proposal for the unfamiliar columns (null: "not needed — leave unread"). */
export interface MappingProposal {
  columns: Record<string, string | null>; proposedBy: string; proposedAt: string; notes: string | null;
}

export interface MappingReview {
  id: number; profileName: string; servicerName: string | null; status: string; createdBy: string | null; createdAt: string | null;
  unmappedColumns: string[]; columns: Array<{ sourceColumn: string; canonicalField: string | null; required?: boolean }>;
  proposal?: MappingProposal | null; mayApprove?: boolean; whyNotApprove?: string | null;
}

export interface PeriodWorkspace {
  facility: { id: number; code: string; name: string; currency: string; collateralType: string; totalCommitment: number; drawnAmount: number };
  policy: VariancePolicy | null;
  availability: Availability;
  periods: CollectionPeriod[];
  currentAuthority: Availability['authority'];
  unattached: Array<Record<string, unknown> & { kind: 'TAPE' | 'CERTIFICATE'; reason: string }>;
  mappingReviews: MappingReview[];
  calendarMissing: boolean;
}

export interface RowIssue {
  id: number; raw_row_number: number; asset_identifier: string | null; reason: string;
  raw_payload: unknown; balance: number | null; status: 'OPEN' | 'CORRECTED' | 'EXCLUDED' | 'SUPERSEDED';
  revisions: unknown;
}

/** `workOnTapeId`: a READY candidate to work on instead of its period's newest tape (R-07). */
export const getPeriodWorkspace = async (facilityId: number, workOnTapeId?: number | null) =>
  (await axios.get<PeriodWorkspace>(`${ROOT}/${facilityId}/periods`, { params: workOnTapeId ? { tape: workOnTapeId } : undefined })).data;

export const getPeriod = async (facilityId: number, periodId: number) =>
  (await axios.get<CollectionPeriod>(`${ROOT}/${facilityId}/periods/${periodId}`)).data;

export const getTapeRows = async (facilityId: number, tapeId: number) =>
  (await axios.get<RowIssue[]>(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/rows`)).data;

/** A tape by file (CSV/XLSX) or by grid: the grid is sent as the CSV built from it, so both take one path. */
export const addPeriodTape = async (facilityId: number, periodId: number, file: File, asOfDate: string, channel: 'UI_UPLOAD' | 'GRID_ENTRY') => {
  const form = new FormData();
  form.append('file', file);
  form.append('asOfDate', asOfDate);
  form.append('channel', channel);
  form.append('periodId', String(periodId));
  return (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/tape`, form)).data;
};

export const previewPeriodTape = async (facilityId: number, periodId: number, file: File, asOfDate: string) => {
  const form = new FormData();
  form.append('file', file); form.append('periodId', String(periodId)); form.append('asOfDate', asOfDate);
  return (await axios.post<TapeEvidenceScope>(`${ROOT}/${facilityId}/periods/tape-preview`, form)).data;
};

export const addKeyedCertificate = async (facilityId: number, body: {
  asOfDate: string; sourceReference: string; borrowingBase: number;
  eligibleAmount?: number | null; ineligibleAmount?: number | null; reserveAmount?: number | null; notes?: string; sourceDocumentJobId?: string;
}) => (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/certificate`, body)).data;

export const releasePeriodTape = async (facilityId: number, tapeId: number, notes: string, expectedPeriodId: number, expectedAsOfDate: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/release`, { notes, expectedPeriodId, expectedAsOfDate, scopeConfirmed: true })).data;

/** F-MK-082: takes an unreleased candidate out of consideration, on record with the reason. */
export const discardPeriodTape = async (facilityId: number, tapeId: number, reason: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/discard`, { reason })).data;

export const correctTapeRow = async (facilityId: number, tapeId: number, rowNumber: number,
  corrections: Record<string, string>, reason: string, evidenceReference?: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/rows/${rowNumber}/correct`,
    { corrections, reason, evidenceReference })).data;

export const proposeRowExclusion = async (facilityId: number, tapeId: number, rowNumber: number, reason: string, evidenceReference?: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/rows/${rowNumber}/exclude`,
    { reason, evidenceReference })).data;

export const decideRowExclusion = async (facilityId: number, tapeId: number, rowNumber: number, approve: boolean, notes?: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/rows/${rowNumber}/exclusion-decision`,
    { approve, notes })).data;

export const approveMapping = async (facilityId: number, profileId: number, columns?: Record<string, string>, notes?: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/mapping-profiles/${profileId}/approve`, { columns, notes })).data;

/** F-MK-062: saves the maker's choices for a checker; an empty choice means "not needed". */
export const proposeMapping = async (facilityId: number, profileId: number, columns: Record<string, string>, notes?: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/mapping-profiles/${profileId}/proposal`, { columns, notes })).data;

export const disposeVariance = async (facilityId: number, comparisonId: number, lineId: number,
  disposition: 'ACCEPTED' | 'CORRECTED' | 'ESCALATED', reason: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/comparisons/${comparisonId}/lines/${lineId}/disposition`,
    { disposition, reason })).data;

export const reconcilePeriod = async (facilityId: number, periodId: number) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/${periodId}/reconcile`, {})).data;

export const decideBorrowingBase = async (facilityId: number, runId: number, decision: 'APPROVE' | 'REJECT', notes: string) =>
  (await axios.post<Record<string, any>>(`${ROOT}/${facilityId}/periods/runs/${runId}/decision`, { decision, notes })).data;

/** The 29 canonical loan fields the parser reads, for grid entry. */
export const GRID_COLUMNS: Array<{ key: string; label: string; required?: boolean; kind: 'text' | 'number' | 'date' | 'flag' }> = [
  { key: 'asset_identifier', label: 'Asset ID', required: true, kind: 'text' },
  { key: 'obligor_identifier', label: 'Obligor ID', kind: 'text' },
  { key: 'obligor_name', label: 'Obligor', kind: 'text' },
  { key: 'current_balance', label: 'Current balance', required: true, kind: 'number' },
  { key: 'original_balance', label: 'Original balance', kind: 'number' },
  { key: 'current_interest_rate_pct', label: 'Rate %', kind: 'number' },
  { key: 'origination_date', label: 'Originated', kind: 'date' },
  { key: 'maturity_date', label: 'Maturity', kind: 'date' },
  { key: 'days_past_due', label: 'Days past due', kind: 'number' },
  { key: 'fico_score', label: 'FICO', kind: 'number' },
  { key: 'ltv_pct', label: 'LTV %', kind: 'number' },
  { key: 'dti_pct', label: 'DTI %', kind: 'number' },
  { key: 'state', label: 'State', kind: 'text' },
  { key: 'country', label: 'Country', kind: 'text' },
  { key: 'vintage_year', label: 'Vintage', kind: 'number' },
  { key: 'asset_status', label: 'Status', kind: 'text' },
  { key: 'is_defaulted', label: 'Defaulted', kind: 'flag' },
  { key: 'is_charged_off', label: 'Charged off', kind: 'flag' },
  { key: 'prepayment_amount', label: 'Prepaid', kind: 'number' },
  { key: 'chargeoff_amount', label: 'Charged-off amt', kind: 'number' },
  { key: 'recovered_amount', label: 'Recovered', kind: 'number' },
];

/** Builds the CSV a grid entry submits — the same bytes a file would carry. */
export const gridToCsv = (rows: Array<Record<string, string>>, columns = GRID_COLUMNS): string => {
  const cell = (value: string | undefined) => {
    const v = (value ?? '').trim();
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  const header = columns.map(c => c.key).join(',');
  const body = rows
    .filter(row => columns.some(c => (row[c.key] ?? '').trim() !== ''))
    .map(row => columns.map(c => cell(row[c.key])).join(','));
  return [header, ...body].join('\n') + '\n';
};

export interface CertificateDocument {
  jobId: string; status: string; fileName: string; hash?: string; uploadedBy?: string;
  error?: string; extracted?: Record<string, unknown>;
}
export const uploadCertificateDocument = async (facilityId: number, file: File) => {
  const form = new FormData(); form.append('file', file);
  return (await axios.post<CertificateDocument>(`${ROOT}/${facilityId}/periods/certificate-documents`, form)).data;
};
export const listCertificateDocuments = async (facilityId: number) =>
  (await axios.get<CertificateDocument[]>(`${ROOT}/${facilityId}/periods/certificate-documents`)).data;
export const getCertificateDocument = async (facilityId: number, jobId: string) =>
  (await axios.get<CertificateDocument>(`${ROOT}/${facilityId}/periods/certificate-documents/${encodeURIComponent(jobId)}`)).data;
export const getCertificateSource = async (facilityId: number, jobId: string) =>
  (await axios.get<Blob>(`${ROOT}/${facilityId}/periods/certificate-documents/${encodeURIComponent(jobId)}/source`, { responseType: 'blob' })).data;
export interface TapeLoanRow {
  id: number; raw_row_number: number; asset_identifier: string; current_balance: number;
  raw_payload: Record<string, unknown>;
}
export const getTapeLoanRows = async (facilityId: number, tapeId: number, offset = 0, search?: string) =>
  (await axios.get<TapeLoanRow[]>(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/assets`, { params: { offset, search: search || undefined } })).data;

export const getComparisonRecord = async (facilityId: number, comparisonId: number) =>
  (await axios.get<{ comparison: Record<string, unknown>; lines: VarianceLine[] }>(`${ROOT}/${facilityId}/periods/comparisons/${comparisonId}`)).data;
export const decidePeriodFraud = async (facilityId: number, findingId: number, action: 'INVESTIGATE' | 'DISMISS' | 'RESOLVE', disposition: string) =>
  (await axios.post(`/api/credit/abf/facilities/${facilityId}/fraud-findings/${findingId}/decision`, { action, disposition })).data;

export const preparePeriodRecomputation = async (facilityId: number, periodId: number) =>
  (await axios.post(`${ROOT}/${facilityId}/periods/${periodId}/recompute`, {})).data;
export const reparsePeriodTape = async (facilityId: number, tapeId: number) =>
  (await axios.post(`${ROOT}/${facilityId}/periods/tapes/${tapeId}/reparse`, {})).data;
